import { type LucideIcon, MoreVertical } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';

export interface KebabMenuAction {
  label: string;
  icon?: LucideIcon;
  destructive?: boolean;
  /** Disable the item in place rather than hiding it — a missing action is harder to reason about. */
  disabled?: boolean;
  /**
   * Why the item is disabled, shown as a tooltip (e.g. "Only admins can delete a routine"). Pair it with
   * `disabled`; a disabled item without a reason leaves the user guessing. Ignored while enabled.
   */
  disabledReason?: string;
  onSelect: () => void;
}

/**
 * The ⋯ overflow menu on a list row or card. Inside a `ListCard`'s `actions` slot it sits above the
 * card's stretched link; inside a table cell the composite `Table` already ignores nested button clicks
 * for row activation.
 *
 * A disabled item stays visible and explains itself on hover and keyboard focus — see the render below.
 */
export function KebabMenu({
  label,
  actions,
}: {
  /** Accessible name for the trigger, e.g. `Actions for Signups`. */
  label: string;
  actions: readonly KebabMenuAction[];
}): React.ReactElement {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="tertiary" size="md" iconOnly aria-label={label} Icon={<MoreVertical />} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action) => {
          const content = (
            <>
              {action.icon && <action.icon className="size-4" />}
              {action.label}
            </>
          );
          if (!(action.disabled && action.disabledReason)) {
            return (
              <DropdownMenuItem
                key={action.label}
                variant={action.destructive ? 'destructive' : 'default'}
                disabled={action.disabled}
                onSelect={action.onSelect}
              >
                {content}
              </DropdownMenuItem>
            );
          }
          // A disabled item WITH a reason is not Radix-`disabled`: that drops it from the menu's roving
          // focus, and Radix swallows Tab while the menu is open, so the reason could only ever be read
          // with a mouse. It stays in the arrow-key order instead, says `aria-disabled`, keeps
          // `data-disabled` for the dimmed look, and refuses its own selection. The ITEM is the tooltip's
          // trigger, so the reason opens on focus and is the focused element's accessible description;
          // `pointer-events-auto` undoes the disabled style's `pointer-events-none`, which would take
          // the hover tooltip away with it.
          return (
            <Tooltip key={action.label}>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  variant={action.destructive ? 'destructive' : 'default'}
                  aria-disabled="true"
                  data-disabled=""
                  className="data-[disabled]:pointer-events-auto"
                  onSelect={(event) => event.preventDefault()}
                >
                  {content}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="left">{action.disabledReason}</TooltipContent>
            </Tooltip>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
